/**
 * Converts table column widths between Lexical's pixels and Froala-compatible percentages.
 * - Lexical only reads/writes `px` (on `<col>`s and every cell), but widths are only ever ratios.
 * - Saved HTML carries percentages; inside the editor they become relative `px` values.
 */

/** `33.3333%` imports as `333.333px`, keeping four decimals of precision in the ratio. */
const PERCENT_SCALE = 10;

type Width = { value: number; unit: 'px' | '%' };
type PlacedCell = { cell: HTMLTableCellElement; column: number; colSpan: number };

const parseWidth = (element: HTMLElement): Width | null => {
  const match = /^(\d+(?:\.\d+)?)(px|%)$/.exec(element.style.width.trim());
  return match ? { value: Number.parseFloat(match[1]), unit: match[2] as Width['unit'] } : null;
};

const formatPercent = (value: number) => `${Number(value.toFixed(4))}%`;

/** Places every cell on the table's grid, honouring `rowspan` / `colspan`. */
const layoutCells = (table: HTMLTableElement) => {
  const occupied: boolean[][] = [];
  const placed: PlacedCell[] = [];

  Array.from(table.rows).forEach((row, rowIndex) => {
    const taken = (occupied[rowIndex] ??= []);
    let column = 0;
    for (const cell of Array.from(row.cells)) {
      while (taken[column]) column++;
      const colSpan = Math.max(1, cell.colSpan);
      const rowSpan = Math.max(1, cell.rowSpan);
      for (let r = 0; r < rowSpan; r++) {
        const spanned = (occupied[rowIndex + r] ??= []);
        for (let c = 0; c < colSpan; c++) spanned[column + c] = true;
      }
      placed.push({ cell, column, colSpan });
      column += colSpan;
    }
  });

  return { placed, columnCount: Math.max(0, ...occupied.map((row) => row.length)) };
};

const ownCols = (table: HTMLTableElement) => Array.from(table.querySelectorAll<HTMLTableColElement>(':scope > colgroup > col'));

/**
 * Export: rewrite every table's widths as percentages, the way Froala stores them.
 * - Mutates Lexical's generated DOM in place, so saving costs no extra HTML parse.
 */
export const tableWidthsToPercent = (root: ParentNode): void => {
  for (const table of Array.from(root.querySelectorAll('table'))) {
    const { placed, columnCount } = layoutCells(table);
    if (columnCount === 0) continue;

    // Resized tables carry a colgroup; anything else splits evenly
    const cols = ownCols(table);
    const widths = cols.length === columnCount ? cols.map((col) => parseWidth(col)?.value ?? Number.NaN) : [];
    const total = widths.reduce((sum, width) => sum + width, 0);
    const shares = total > 0 ? widths.map((width) => (width / total) * 100) : Array<number>(columnCount).fill(100 / columnCount);

    cols.forEach((col, index) => (col.style.width = formatPercent(shares[index])));
    for (const { cell, column, colSpan } of placed) {
      let share = shares[column];
      for (let c = 1; c < colSpan; c++) share += shares[column + c];
      cell.style.width = formatPercent(share);
    }
    table.style.width = '100%';
  }
};

/** Import: turn percentage (or per-cell) widths into the `px` colgroup Lexical understands. */
export const tableWidthsFromPercent = (doc: Document): void => {
  for (const table of Array.from(doc.querySelectorAll('table'))) {
    const { placed, columnCount } = layoutCells(table);
    if (columnCount === 0) continue;

    const cols = ownCols(table);
    // No colgroup (e.g. Froala content): read each column from its first single-span cell
    let sources: (HTMLElement | undefined)[] = cols;
    if (cols.length !== columnCount) {
      sources = Array<HTMLElement | undefined>(columnCount);
      for (const { cell, column, colSpan } of placed) if (colSpan === 1) sources[column] ??= cell;
    }
    const widths = sources.map((source) => (source ? parseWidth(source) : null));

    const complete = widths.every((width): width is Width => width !== null && width.value > 0);
    const sameUnit = complete && widths.every((width) => width.unit === widths[0]?.unit);
    const values = sameUnit ? widths.map(({ value, unit }) => (unit === '%' ? value * PERCENT_SCALE : value)) : null;

    table.querySelector(':scope > colgroup')?.remove();
    // Even columns need no colgroup; the editor's fixed layout already splits evenly
    if (values && values.some((value) => Math.abs(value - values[0]) > 0.01)) {
      const colgroup = doc.createElement('colgroup');
      for (const value of values) {
        const col = doc.createElement('col');
        col.style.width = `${Number(value.toFixed(3))}px`;
        colgroup.append(col);
      }
      table.prepend(colgroup);
    }

    // Per-cell widths would otherwise be imported as fixed `px` on each cell
    for (const { cell } of placed) cell.style.removeProperty('width');
    table.style.removeProperty('width');
  }
};
